import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MobileOutlined, DesktopOutlined } from "@ant-design/icons";
import { Segmented } from "antd";
import { CAPTURE_MODES } from "./constants";
import styles from "./style.module.scss";

const VoiceWidgetPreview = ({
  prompt,
  modes = [CAPTURE_MODES.VOICE, CAPTURE_MODES.TEXT],
  displayMode = "popup",
  maxDurationSeconds = 120,
}) => {
  const { t } = useTranslation();
  const allowVoice = modes.includes(CAPTURE_MODES.VOICE);
  const allowText = modes.includes(CAPTURE_MODES.TEXT);
  const [scene, setScene] = useState("web");
  const [activeMode, setActiveMode] = useState(
    allowVoice ? CAPTURE_MODES.VOICE : CAPTURE_MODES.TEXT,
  );

  useEffect(() => {
    if (allowVoice && !allowText) setActiveMode(CAPTURE_MODES.VOICE);
    else if (!allowVoice && allowText) setActiveMode(CAPTURE_MODES.TEXT);
  }, [allowVoice, allowText]);

  const promptText =
    (prompt || "").trim() ||
    t("voiceCaptureManagement.preview_prompt_placeholder");

  const modeLabel = useMemo(() => {
    if (displayMode === "fullscreen") {
      return t("voiceCaptureManagement.display_fullscreen");
    }
    if (displayMode === "inline") {
      return t("voiceCaptureManagement.display_inline");
    }
    return t("voiceCaptureManagement.display_popup");
  }, [displayMode, t]);

  const widget = (
    <div
      className={[
        styles.previewWidget,
        styles[`previewWidget_${displayMode}`],
        scene === "mobile" ? styles.previewWidgetMobile : "",
      ].join(" ")}
    >
      <div className={styles.previewWidgetPanel}>
        <div className={styles.previewWidgetHeader}>
          <span className={styles.previewWidgetBrand}>Pivony Capture</span>
          <button type="button" className={styles.previewWidgetClose} aria-hidden>
            ×
          </button>
        </div>
        <div className={styles.previewWidgetBody}>
          <p className={styles.previewWidgetPrompt}>{promptText}</p>
          {allowVoice && allowText ? (
            <div className={styles.previewWidgetModes}>
              <button
                type="button"
                className={
                  activeMode === CAPTURE_MODES.VOICE
                    ? styles.previewModeBtnActive
                    : styles.previewModeBtn
                }
                onClick={() => setActiveMode(CAPTURE_MODES.VOICE)}
              >
                {t("voiceCaptureManagement.mode_voice")}
              </button>
              <button
                type="button"
                className={
                  activeMode === CAPTURE_MODES.TEXT
                    ? styles.previewModeBtnActive
                    : styles.previewModeBtn
                }
                onClick={() => setActiveMode(CAPTURE_MODES.TEXT)}
              >
                {t("voiceCaptureManagement.mode_text")}
              </button>
            </div>
          ) : null}
          {activeMode === CAPTURE_MODES.VOICE && allowVoice ? (
            <div className={styles.previewVoicePane}>
              <button type="button" className={styles.previewRecordBtn}>
                {t("voiceCaptureManagement.preview_tap_record")}
              </button>
              <div className={styles.previewTimer}>0:00</div>
              <p className={styles.previewHint}>
                {t("voiceCaptureManagement.preview_voice_hint", {
                  seconds: maxDurationSeconds,
                })}
              </p>
            </div>
          ) : null}
          {activeMode === CAPTURE_MODES.TEXT && allowText ? (
            <textarea
              className={styles.previewTextarea}
              rows={4}
              readOnly
              placeholder={t("voiceCaptureManagement.preview_text_placeholder")}
            />
          ) : null}
          <button type="button" className={styles.previewSubmitBtn}>
            {t("voiceCaptureManagement.preview_send")}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.previewShell}>
      <div className={styles.previewToolbar}>
        <span className={styles.previewTitle}>
          {t("voiceCaptureManagement.preview_title")}
        </span>
        <Segmented
          size="small"
          value={scene}
          onChange={setScene}
          options={[
            {
              label: t("voiceCaptureManagement.preview_web"),
              value: "web",
              icon: <DesktopOutlined />,
            },
            {
              label: t("voiceCaptureManagement.preview_app"),
              value: "mobile",
              icon: <MobileOutlined />,
            },
          ]}
        />
      </div>
      <p className={styles.previewCaption}>
        {t("voiceCaptureManagement.preview_caption", { mode: modeLabel })}
      </p>

      {scene === "web" ? (
        <div className={styles.previewBrowser}>
          <div className={styles.previewBrowserChrome}>
            <span className={styles.previewDot} />
            <span className={styles.previewDot} />
            <span className={styles.previewDot} />
            <span className={styles.previewUrl}>www.your-hotel.com/checkout</span>
          </div>
          <div className={styles.previewBrowserBody}>
            <div className={styles.previewFakePage}>
              <div className={styles.previewFakeHero} />
              <div className={styles.previewFakeLine} />
              <div className={styles.previewFakeLineShort} />
              {displayMode === "inline" ? (
                <div className={styles.previewInlineSlot}>{widget}</div>
              ) : (
                widget
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.previewPhone}>
          <div className={styles.previewPhoneNotch} />
          <div className={styles.previewPhoneScreen}>
            <div className={styles.previewAppHeader}>
              {t("voiceCaptureManagement.preview_app_name")}
            </div>
            <div className={styles.previewAppContent}>
              <div className={styles.previewFakeLine} />
              <div className={styles.previewFakeLineShort} />
            </div>
            {widget}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceWidgetPreview;
